import { assets } from "@/content/assets";
import {
  aboutClosingCta,
  aboutHero,
  aboutHeroStats,
  aboutIntro,
  aboutLeadership,
  aboutMission,
  aboutStoryOverview,
  aboutStoryQuote,
  aboutStoryTimeline,
  aboutValues,
  aboutValuesFooter,
  aboutVision,
  fieldPhotos,
} from "@/content/about";
import { homeAboutStory, homeDonate, homeHero, homeImpact, homeTestimonials, buildHomeImpactMetrics, featuredProgramSlugs, galleryPhotos } from "@/content/home";
import { impactCategories as staticImpactCategories } from "@/content/impact";
import { programs as staticPrograms } from "@/content/programs/all-programs";
import type { Program } from "@/content/programs/types";
import { site } from "@/content/site";
import { teamMembers, founder } from "@/content/team";
import { normalizeAboutValueIcon } from "@/lib/about-value-icons";
import { normalizeFounderCardTemplate } from "@/lib/founder-card-templates";
import type {
  AboutContent,
  AboutGalleryContent,
  AboutHeroContent,
  AboutLeadershipContent,
  Event,
  HomeAboutStoryContent,
  HomeContent,
  HomeDonateSection,
  HomeFeaturedProgramsContent,
  HomeHeroContent,
  HomeImpactContent,
  HomeImpactMetric,
  HomeMiniGalleryContent,
  HomeSectionCopy,
  ImpactContent,
  ProgramsContent,
  Stats,
  Testimonial,
} from "@/lib/api-types";

function nonEmpty(value: string | undefined | null, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
}

function isUsableImageUrl(value: string | undefined | null): boolean {
  const trimmed = value?.trim();
  if (!trimmed) return false;
  return (
    trimmed.startsWith("/") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:image/")
  );
}

const LEGACY_BROKEN_TESTIMONIAL_IMAGES = new Set([
  "/images/contact-1.jpg",
  "/images/contact-2.jpg",
]);

function resolveTestimonialImage(
  image: string | undefined | null,
  fallback: string
): string {
  const trimmed = image?.trim() ?? "";
  if (
    !trimmed ||
    !isUsableImageUrl(trimmed) ||
    LEGACY_BROKEN_TESTIMONIAL_IMAGES.has(trimmed)
  ) {
    return fallback;
  }
  return trimmed;
}

/** Accept hosted paths/URLs and mock data URLs used by local CMS uploads. */
export function isHomeHeroImageUrl(value: string | undefined | null): boolean {
  const trimmed = value?.trim();
  if (!trimmed) return false;
  return (
    trimmed.startsWith("/") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:image/")
  );
}

export const DEFAULT_HOME_HERO: HomeHeroContent = {
  eyebrow: site.legalStatus,
  headingLines: [...homeHero.headingLines],
  subtitle: homeHero.subtitle,
  heroImage: assets.hero,
  heroImageAlt:
    "Children embracing with Charminar, Hyderabad — representing hope and community impact",
  primaryCta: { label: "Donate Now", href: "/donate" },
  secondaryCta: { label: "Our Programs", href: "/programs" },
};

/** Default public hero background (`/images/home-hero-charminar.png`). */
export const DEFAULT_HOME_HERO_IMAGE = DEFAULT_HOME_HERO.heroImage;

export function resolveHomeHeroImage(value?: string | null): string {
  return isHomeHeroImageUrl(value) ? value!.trim() : DEFAULT_HOME_HERO_IMAGE;
}

/** Keep valid hero URLs (including mock data URLs); clear anything else. */
export function sanitizeStoredHomeHeroImage(value?: string | null): string {
  return isHomeHeroImageUrl(value) ? value!.trim() : "";
}

export const DEFAULT_EVENTS_SECTION: HomeSectionCopy = {
  eyebrow: "Community Activities",
  title: "Events & Activities",
  subtitle:
    "Community events, drives, and initiatives making an impact across our programs.",
};

export const DEFAULT_TESTIMONIALS_SECTION: HomeSectionCopy = {
  eyebrow: "Testimonials",
  title: "What people say",
  subtitle: "Voices from donors and community partners.",
};

const DEFAULT_IMPACT_METRICS: HomeImpactMetric[] = buildHomeImpactMetrics().map(
  (m) => ({
    id: m.id,
    label: m.label,
    value: m.value,
    suffix: m.suffix,
    featured: m.featured,
  })
);

export const DEFAULT_HOME_IMPACT: HomeImpactContent = {
  eyebrow: homeImpact.eyebrow,
  title: homeImpact.title,
  subtitle: homeImpact.subtitle,
  cta: homeImpact.cta,
  ctaHref: homeImpact.ctaHref,
  metrics: DEFAULT_IMPACT_METRICS.map((m) => ({ ...m })),
};

export const DEFAULT_HOME_ABOUT_STORY: HomeAboutStoryContent = {
  eyebrow: homeAboutStory.eyebrow,
  title: homeAboutStory.title,
  missionImage: homeAboutStory.missionImage,
  missionImageAlt: homeAboutStory.missionImageAlt,
  missionStatement: homeAboutStory.missionStatement,
  highlightKeywords: [...homeAboutStory.highlightKeywords],
  timeline: homeAboutStory.timeline.map((t) => ({ ...t })),
  ctaLabel: "Read our full story",
  ctaHref: "/about",
};

export const DEFAULT_HOME_ABOUT_IMAGE = DEFAULT_HOME_ABOUT_STORY.missionImage;

export const DEFAULT_HOME_DONATE: HomeDonateSection = {
  title: homeDonate.title,
  subtitle: homeDonate.subtitle,
  cta: homeDonate.cta,
  ctaHref: "/donate",
};

export const DEFAULT_HOME_FEATURED_PROGRAMS: HomeFeaturedProgramsContent = {
  eyebrow: "Our Programs",
  title: "Making a Difference Together",
  subtitle: "Four core initiatives addressing urgent community needs",
  slugs: [...featuredProgramSlugs],
  ctaLabel: "View all programs",
  ctaHref: "/programs",
};

export const DEFAULT_HOME_MINI_GALLERY: HomeMiniGalleryContent = {
  eyebrow: "In the field",
  title: "Moments of seva",
  subtitle:
    "Glimpses from the field — communities, programs, and lives touched.",
  ctaLabel: "View gallery",
  ctaHref: "/impact",
  images: galleryPhotos.slice(0, 5).map((src, index) => ({
    id: `mini-${index + 1}`,
    src,
    alt: `Community seva moment ${index + 1}`,
    sortOrder: index,
  })),
};

export const DEFAULT_HOME_CONTENT: HomeContent = {
  hero: DEFAULT_HOME_HERO,
  eventsSection: DEFAULT_EVENTS_SECTION,
  testimonialsSection: DEFAULT_TESTIMONIALS_SECTION,
  impact: DEFAULT_HOME_IMPACT,
  aboutStory: DEFAULT_HOME_ABOUT_STORY,
  donateSection: DEFAULT_HOME_DONATE,
  featuredPrograms: DEFAULT_HOME_FEATURED_PROGRAMS,
  miniGallery: DEFAULT_HOME_MINI_GALLERY,
};

function mergeHeadingLines(partial?: string[] | null): string[] {
  const defaults = DEFAULT_HOME_HERO.headingLines;
  if (!partial?.length) {
    return [...defaults];
  }

  const merged = defaults.map((defaultLine, index) =>
    nonEmpty(partial[index], defaultLine)
  );

  if (partial.length > defaults.length) {
    for (let i = defaults.length; i < partial.length; i += 1) {
      const line = partial[i]?.trim();
      if (line) merged.push(line);
    }
  }

  const usable = merged.map((line) => line.trim()).filter(Boolean);
  return usable.length > 0 ? usable : [...defaults];
}

function isHeroEffectivelyEmpty(
  partial?: Partial<HomeHeroContent> | null
): boolean {
  if (!partial) return true;

  const hasHeading = partial.headingLines?.some((line) => line.trim().length > 0);
  const hasSubtitle = Boolean(partial.subtitle?.trim());
  const hasImage = isHomeHeroImageUrl(partial.heroImage);

  return !hasHeading && !hasSubtitle && !hasImage;
}

/** Static fallback events when admin has none published (matches original seed content). */
export const FALLBACK_EVENTS: Event[] = [
  {
    id: "fallback_evt_health_camp",
    title: "Health Camp 2026",
    date: "March 10, 2026",
    description:
      "Free health checkups and medical consultations for rural communities.",
    status: "upcoming",
    items: "Free Medical Camp",
    images: [assets.programs.healthcare],
    heroImage: assets.eventsHero,
    featured: true,
  },
  {
    id: "fallback_evt_tree_drive",
    title: "Community Tree Plantation Drive",
    date: "April 22, 2026",
    description:
      "Join volunteers to plant native trees and restore green cover in local neighborhoods.",
    status: "upcoming",
    items: "1,000+ saplings",
    images: [assets.programs.goshala],
    heroImage: assets.eventsHero,
    featured: false,
  },
];

export function mergeSectionCopy(
  partial: Partial<HomeSectionCopy> | undefined,
  defaults: HomeSectionCopy
): HomeSectionCopy {
  return {
    eyebrow: nonEmpty(partial?.eyebrow, defaults.eyebrow),
    title: nonEmpty(partial?.title, defaults.title),
    subtitle: nonEmpty(partial?.subtitle, defaults.subtitle),
  };
}

export function mergeHomeHero(
  partial?: Partial<HomeHeroContent> | null
): HomeHeroContent {
  if (isHeroEffectivelyEmpty(partial)) {
    return { ...DEFAULT_HOME_HERO, headingLines: [...DEFAULT_HOME_HERO.headingLines] };
  }

  const defaults = DEFAULT_HOME_HERO;

  return {
    eyebrow: nonEmpty(partial?.eyebrow, defaults.eyebrow),
    headingLines: mergeHeadingLines(partial?.headingLines),
    subtitle: nonEmpty(partial?.subtitle, defaults.subtitle),
    heroImage: resolveHomeHeroImage(partial?.heroImage),
    heroImageAlt: nonEmpty(partial?.heroImageAlt, defaults.heroImageAlt),
    primaryCta: {
      label: nonEmpty(partial?.primaryCta?.label, defaults.primaryCta.label),
      href: nonEmpty(partial?.primaryCta?.href, defaults.primaryCta.href),
    },
    secondaryCta: {
      label: nonEmpty(
        partial?.secondaryCta?.label,
        defaults.secondaryCta.label
      ),
      href: nonEmpty(partial?.secondaryCta?.href, defaults.secondaryCta.href),
    },
  };
}

function mergeImpactMetrics(
  partial?: HomeImpactMetric[] | null
): HomeImpactMetric[] {
  if (!partial?.length) {
    return DEFAULT_IMPACT_METRICS.map((m) => ({ ...m }));
  }
  const byId = new Map(partial.map((m) => [m.id, m]));
  const merged = DEFAULT_IMPACT_METRICS.map((defaults) => {
    const item = byId.get(defaults.id);
    byId.delete(defaults.id);
    return {
      id: defaults.id,
      label: nonEmpty(item?.label, defaults.label),
      value:
        typeof item?.value === "number" && Number.isFinite(item.value)
          ? item.value
          : defaults.value,
      suffix: item?.suffix != null ? String(item.suffix) : defaults.suffix,
      featured: item?.featured ?? defaults.featured,
    };
  });
  for (const leftover of byId.values()) {
    merged.push({
      id: leftover.id,
      label: leftover.label,
      value: leftover.value,
      suffix: leftover.suffix,
      featured: leftover.featured,
    });
  }
  return merged;
}

export function mergeHomeImpact(
  partial?: Partial<HomeImpactContent> | null
): HomeImpactContent {
  const d = DEFAULT_HOME_IMPACT;
  return {
    eyebrow: nonEmpty(partial?.eyebrow, d.eyebrow),
    title: nonEmpty(partial?.title, d.title),
    subtitle: nonEmpty(partial?.subtitle, d.subtitle),
    cta: nonEmpty(partial?.cta, d.cta),
    ctaHref: nonEmpty(partial?.ctaHref, d.ctaHref),
    metrics: mergeImpactMetrics(partial?.metrics),
  };
}

/** Apply live stats overlay for the "lives" metric when available. */
export function resolveHomeImpactMetrics(
  impact: HomeImpactContent,
  stats?: Stats
): HomeImpactMetric[] {
  return impact.metrics.map((m) =>
    m.id === "lives" && stats?.livesImpacted != null
      ? { ...m, value: stats.livesImpacted }
      : { ...m }
  );
}

export function mergeHomeAboutStory(
  partial?: Partial<HomeAboutStoryContent> | null
): HomeAboutStoryContent {
  const d = DEFAULT_HOME_ABOUT_STORY;
  const keywords =
    partial?.highlightKeywords
      ?.map((k) => k.trim())
      .filter(Boolean) ?? [];
  const timeline =
    partial?.timeline?.length
      ? partial.timeline.map((t, i) => {
          const fallback = d.timeline[i] ?? d.timeline[d.timeline.length - 1];
          return {
            year: nonEmpty(t.year, fallback.year),
            title: nonEmpty(t.title, fallback.title),
            description: nonEmpty(t.description, fallback.description),
          };
        })
      : d.timeline.map((t) => ({ ...t }));

  return {
    eyebrow: nonEmpty(partial?.eyebrow, d.eyebrow),
    title: nonEmpty(partial?.title, d.title),
    missionImage: isHomeHeroImageUrl(partial?.missionImage)
      ? partial!.missionImage!.trim()
      : d.missionImage,
    missionImageAlt: nonEmpty(partial?.missionImageAlt, d.missionImageAlt),
    missionStatement: nonEmpty(partial?.missionStatement, d.missionStatement),
    highlightKeywords: keywords.length > 0 ? keywords : [...d.highlightKeywords],
    timeline,
    ctaLabel: nonEmpty(partial?.ctaLabel, d.ctaLabel),
    ctaHref: nonEmpty(partial?.ctaHref, d.ctaHref),
  };
}

export function mergeHomeContent(
  partial?: Partial<HomeContent> | null
): HomeContent {
  return {
    hero: mergeHomeHero(partial?.hero),
    eventsSection: mergeSectionCopy(
      partial?.eventsSection,
      DEFAULT_EVENTS_SECTION
    ),
    testimonialsSection: mergeSectionCopy(
      partial?.testimonialsSection,
      DEFAULT_TESTIMONIALS_SECTION
    ),
    impact: mergeHomeImpact(partial?.impact),
    aboutStory: mergeHomeAboutStory(partial?.aboutStory),
    donateSection: {
      title: nonEmpty(partial?.donateSection?.title, DEFAULT_HOME_DONATE.title),
      subtitle: nonEmpty(
        partial?.donateSection?.subtitle,
        DEFAULT_HOME_DONATE.subtitle
      ),
      cta: nonEmpty(partial?.donateSection?.cta, DEFAULT_HOME_DONATE.cta),
      ctaHref: nonEmpty(
        partial?.donateSection?.ctaHref,
        DEFAULT_HOME_DONATE.ctaHref
      ),
    },
    featuredPrograms: {
      eyebrow: nonEmpty(
        partial?.featuredPrograms?.eyebrow,
        DEFAULT_HOME_FEATURED_PROGRAMS.eyebrow
      ),
      title: nonEmpty(
        partial?.featuredPrograms?.title,
        DEFAULT_HOME_FEATURED_PROGRAMS.title
      ),
      subtitle: nonEmpty(
        partial?.featuredPrograms?.subtitle,
        DEFAULT_HOME_FEATURED_PROGRAMS.subtitle
      ),
      slugs:
        partial?.featuredPrograms?.slugs &&
        partial.featuredPrograms.slugs.length > 0
          ? [...partial.featuredPrograms.slugs]
          : [...DEFAULT_HOME_FEATURED_PROGRAMS.slugs],
      ctaLabel: nonEmpty(
        partial?.featuredPrograms?.ctaLabel,
        DEFAULT_HOME_FEATURED_PROGRAMS.ctaLabel
      ),
      ctaHref: nonEmpty(
        partial?.featuredPrograms?.ctaHref,
        DEFAULT_HOME_FEATURED_PROGRAMS.ctaHref
      ),
    },
    miniGallery: mergeHomeMiniGallery(partial?.miniGallery),
  };
}

function mergeHomeMiniGallery(
  partial?: Partial<HomeMiniGalleryContent> | null
): HomeMiniGalleryContent {
  const d = DEFAULT_HOME_MINI_GALLERY;
  const images =
    partial?.images && partial.images.length > 0
      ? partial.images
          .map((img, index) => ({
            id: nonEmpty(img.id, `mini-${index + 1}`),
            src: isUsableImageUrl(img.src) ? img.src.trim() : d.images[index % d.images.length]?.src ?? "",
            alt: nonEmpty(img.alt, `Community seva moment ${index + 1}`),
            sortOrder: typeof img.sortOrder === "number" ? img.sortOrder : index,
          }))
          .filter((img) => img.src)
          .sort((a, b) => a.sortOrder - b.sortOrder)
      : d.images.map((img) => ({ ...img }));
  return {
    eyebrow: nonEmpty(partial?.eyebrow, d.eyebrow),
    title: nonEmpty(partial?.title, d.title),
    subtitle: nonEmpty(partial?.subtitle, d.subtitle),
    ctaLabel: nonEmpty(partial?.ctaLabel, d.ctaLabel),
    ctaHref: nonEmpty(partial?.ctaHref, d.ctaHref),
    images: images.length > 0 ? images : d.images.map((img) => ({ ...img })),
  };
}

export const DEFAULT_ABOUT_CONTENT: AboutContent = {
  hero: {
    eyebrow: aboutHero.eyebrow,
    title: aboutHero.title,
    accentWord: aboutHero.accentWord,
    subtitle: aboutHero.subtitle,
    imageSrc: aboutHero.image.src,
    imageAlt: aboutHero.image.alt,
    primaryCta: {
      label: aboutHero.ctas.primary.label,
      href: aboutHero.ctas.primary.href,
    },
    secondaryCta: {
      label: aboutHero.ctas.secondary.label,
      href: aboutHero.ctas.secondary.href,
    },
    stats: aboutHeroStats.map((s) => ({ value: s.value, label: s.label })),
  },
  intro: {
    headline: aboutIntro.headline,
    lead: aboutIntro.lead,
    paragraphs: [...aboutIntro.paragraphs],
    founderCard: {
      name: founder.name,
      role: founder.role,
      quote: founder.quote,
      imageSrc: founder.image,
      imageAlt: founder.name,
      template: "glass-portrait",
    },
  },
  purpose: {
    vision: { ...aboutVision },
    mission: { ...aboutMission },
  },
  story: {
    overviewTitle: aboutStoryOverview.title,
    overviewParagraphs: [...aboutStoryOverview.paragraphs],
    timeline: aboutStoryTimeline.map((t) => ({ ...t })),
    quote: aboutStoryQuote.quote,
    closing: aboutStoryQuote.closing,
  },
  values: {
    items: aboutValues.map((v) => ({
      title: v.title,
      description: v.description,
      icon: normalizeAboutValueIcon(v.icon),
    })),
    footer: aboutValuesFooter,
  },
  closingCta: {
    title: aboutClosingCta.title,
    description: aboutClosingCta.description,
    primary: { ...aboutClosingCta.primary },
    secondary: { ...aboutClosingCta.secondary },
  },
  gallery: {
    title: "In the Field",
    subtitle: "Moments from our community work",
    images: fieldPhotos.map((src, index) => ({
      id: `gallery_${index + 1}`,
      src,
      alt: `Community field work ${index + 1}`,
      sortOrder: index,
    })),
  },
  leadership: {
    title: aboutLeadership.title,
    accentWord: aboutLeadership.accentWord,
    subtitle: aboutLeadership.subtitle,
    members: teamMembers.map((m, index) => ({
      id: `member_${index + 1}`,
      name: m.name,
      role: m.role,
      bio: m.bio,
      image: m.image,
      sortOrder: index,
      published: true,
    })),
  },
};

function resolveAboutImage(value: string | undefined | null, fallback: string): string {
  return isUsableImageUrl(value) ? value!.trim() : fallback;
}

export function mergeAboutHero(
  partial?: Partial<AboutHeroContent> | null
): AboutHeroContent {
  const d = DEFAULT_ABOUT_CONTENT.hero;
  return {
    eyebrow: nonEmpty(partial?.eyebrow, d.eyebrow),
    title: nonEmpty(partial?.title, d.title),
    accentWord: nonEmpty(partial?.accentWord, d.accentWord),
    subtitle: nonEmpty(partial?.subtitle, d.subtitle),
    imageSrc: resolveAboutImage(partial?.imageSrc, d.imageSrc),
    imageAlt: nonEmpty(partial?.imageAlt, d.imageAlt),
    primaryCta: {
      label: nonEmpty(partial?.primaryCta?.label, d.primaryCta.label),
      href: nonEmpty(partial?.primaryCta?.href, d.primaryCta.href),
    },
    secondaryCta: {
      label: nonEmpty(partial?.secondaryCta?.label, d.secondaryCta.label),
      href: nonEmpty(partial?.secondaryCta?.href, d.secondaryCta.href),
    },
    stats:
      partial?.stats && partial.stats.length > 0
        ? partial.stats.map((s) => ({
            value: nonEmpty(s.value, ""),
            label: nonEmpty(s.label, ""),
          }))
        : d.stats.map((s) => ({ ...s })),
  };
}

export function mergeAboutGallery(
  partial?: Partial<AboutGalleryContent> | null
): AboutGalleryContent {
  const d = DEFAULT_ABOUT_CONTENT.gallery;
  const images =
    partial?.images && partial.images.length > 0
      ? [...partial.images].sort((a, b) => a.sortOrder - b.sortOrder)
      : d.images.map((i) => ({ ...i }));
  return {
    title: nonEmpty(partial?.title, d.title),
    subtitle: nonEmpty(partial?.subtitle, d.subtitle),
    images,
  };
}

export function mergeAboutLeadership(
  partial?: Partial<AboutLeadershipContent> | null
): AboutLeadershipContent {
  const d = DEFAULT_ABOUT_CONTENT.leadership;
  const legacyImages = new Set([
    "/assets/CEO.webp",
    "/assets/COO.webp",
    "/assets/Director.webp",
    "/assets/CTO.webp",
  ]);

  let members: AboutLeadershipContent["members"];
  if (partial?.members && partial.members.length > 0) {
    const byId = new Map(partial.members.map((m) => [m.id, m]));
    const byName = new Map(partial.members.map((m) => [m.name, m]));
    const usedIds = new Set<string>();
    const usedNames = new Set<string>();
    members = d.members.map((defaultMember) => {
      const stored =
        byId.get(defaultMember.id) ?? byName.get(defaultMember.name);
      if (!stored) return { ...defaultMember };
      const image =
        !stored.image?.trim() || legacyImages.has(stored.image)
          ? defaultMember.image
          : stored.image;
      usedIds.add(defaultMember.id);
      usedNames.add(defaultMember.name);
      return {
        ...defaultMember,
        ...stored,
        id: defaultMember.id,
        image,
        sortOrder: defaultMember.sortOrder,
      };
    });
    for (const m of partial.members) {
      if (usedIds.has(m.id) || usedNames.has(m.name)) continue;
      members.push({ ...m });
    }
    members = members
      .filter((m) => m.published !== false)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  } else {
    members = d.members.map((m) => ({ ...m }));
  }

  return {
    title: nonEmpty(partial?.title, d.title),
    accentWord: nonEmpty(partial?.accentWord, d.accentWord),
    subtitle: nonEmpty(partial?.subtitle, d.subtitle),
    members,
  };
}

export function mergeAboutContent(
  partial?: Partial<AboutContent> | null
): AboutContent {
  const d = DEFAULT_ABOUT_CONTENT;
  return {
    hero: mergeAboutHero(partial?.hero),
    intro: {
      headline: nonEmpty(partial?.intro?.headline, d.intro.headline),
      lead: nonEmpty(partial?.intro?.lead, d.intro.lead),
      paragraphs:
        partial?.intro?.paragraphs && partial.intro.paragraphs.length > 0
          ? partial.intro.paragraphs
          : [...d.intro.paragraphs],
      founderCard: {
        name: nonEmpty(
          partial?.intro?.founderCard?.name,
          d.intro.founderCard.name
        ),
        role: nonEmpty(
          partial?.intro?.founderCard?.role,
          d.intro.founderCard.role
        ),
        quote: nonEmpty(
          partial?.intro?.founderCard?.quote,
          d.intro.founderCard.quote
        ),
        imageSrc: isUsableImageUrl(partial?.intro?.founderCard?.imageSrc)
          ? partial!.intro!.founderCard!.imageSrc.trim()
          : d.intro.founderCard.imageSrc,
        imageAlt: nonEmpty(
          partial?.intro?.founderCard?.imageAlt,
          d.intro.founderCard.imageAlt
        ),
        template: normalizeFounderCardTemplate(
          partial?.intro?.founderCard?.template
        ),
      },
    },
    purpose: {
      vision: {
        title: nonEmpty(partial?.purpose?.vision?.title, d.purpose.vision.title),
        description: nonEmpty(
          partial?.purpose?.vision?.description,
          d.purpose.vision.description
        ),
      },
      mission: {
        title: nonEmpty(
          partial?.purpose?.mission?.title,
          d.purpose.mission.title
        ),
        description: nonEmpty(
          partial?.purpose?.mission?.description,
          d.purpose.mission.description
        ),
      },
    },
    story: {
      overviewTitle: nonEmpty(
        partial?.story?.overviewTitle,
        d.story.overviewTitle
      ),
      overviewParagraphs:
        partial?.story?.overviewParagraphs &&
        partial.story.overviewParagraphs.length > 0
          ? partial.story.overviewParagraphs
          : [...d.story.overviewParagraphs],
      timeline:
        partial?.story?.timeline && partial.story.timeline.length > 0
          ? partial.story.timeline.map((t) => ({ ...t }))
          : d.story.timeline.map((t) => ({ ...t })),
      quote: nonEmpty(partial?.story?.quote, d.story.quote),
      closing: nonEmpty(partial?.story?.closing, d.story.closing),
    },
    values: {
      items:
        partial?.values?.items && partial.values.items.length > 0
          ? partial.values.items.map((i) => ({
              ...i,
              icon: normalizeAboutValueIcon(i.icon),
            }))
          : d.values.items.map((i) => ({ ...i })),
      footer: nonEmpty(partial?.values?.footer, d.values.footer),
    },
    closingCta: {
      title: nonEmpty(partial?.closingCta?.title, d.closingCta.title),
      description: nonEmpty(
        partial?.closingCta?.description,
        d.closingCta.description
      ),
      primary: {
        label: nonEmpty(
          partial?.closingCta?.primary?.label,
          d.closingCta.primary.label
        ),
        href: nonEmpty(
          partial?.closingCta?.primary?.href,
          d.closingCta.primary.href
        ),
      },
      secondary: {
        label: nonEmpty(
          partial?.closingCta?.secondary?.label,
          d.closingCta.secondary.label
        ),
        href: nonEmpty(
          partial?.closingCta?.secondary?.href,
          d.closingCta.secondary.href
        ),
      },
    },
    gallery: mergeAboutGallery(partial?.gallery),
    leadership: mergeAboutLeadership(partial?.leadership),
  };
}

function staticTestimonialsAsPublic(): Testimonial[] {
  return homeTestimonials.map((item, index) => ({
    id: `fallback_tst_${index + 1}`,
    quote: item.quote,
    name: item.name,
    role: item.role,
    image: item.image,
  }));
}

export function withTestimonialFallbacks(items: Testimonial[]): Testimonial[] {
  if (items.length === 0) {
    return staticTestimonialsAsPublic();
  }

  return items.map((item, index) => {
    const seed = homeTestimonials[index % homeTestimonials.length];
    const contactImage =
      assets.contact[index % assets.contact.length] ?? assets.contact[0];

    return {
      ...item,
      quote: nonEmpty(item.quote, seed?.quote ?? ""),
      name: nonEmpty(item.name, seed?.name ?? "Community member"),
      role: nonEmpty(item.role, seed?.role ?? "Supporter"),
      image: resolveTestimonialImage(
        item.image,
        seed?.image ?? contactImage
      ),
    };
  });
}

export function withEventFallbacks(events: Event[]): Event[] {
  return events.map((event, index) => {
    const fallbackImage =
      assets.fieldWork[index % assets.fieldWork.length] ?? assets.eventsHero;
    const images = event.images?.filter((src) => src.trim().length > 0) ?? [];
    const primaryImage = images[0] ?? fallbackImage;

    return {
      ...event,
      title: nonEmpty(event.title, "Community event"),
      // Keep empty dates empty — UI omits the label (do not invent "Coming soon").
      date: event.date?.trim() ?? "",
      description: nonEmpty(
        event.description,
        "Join us for an upcoming community activity."
      ),
      images: images.length > 0 ? images : [fallbackImage],
      heroImage: nonEmpty(event.heroImage ?? undefined, primaryImage) || null,
    };
  });
}

export function withEventsListFallback(events: Event[]): Event[] {
  const normalized = withEventFallbacks(events);
  return normalized.length > 0 ? normalized : FALLBACK_EVENTS;
}

export function resolveEventsHeroImage(events: Event[]): string {
  const publishedUpcoming = events.filter(
    (e) => e.status.toLowerCase() === "upcoming"
  );
  const featured = publishedUpcoming.find((e) => e.featured && e.heroImage);
  if (featured?.heroImage) {
    return featured.heroImage;
  }
  const withHero = publishedUpcoming.find((e) => e.heroImage);
  return withHero?.heroImage ?? assets.eventsHero;
}

export const DEFAULT_PROGRAMS_CONTENT: ProgramsContent = {
  programs: staticPrograms.map((program) => ({
    ...program,
    features: [...program.features],
    overview: {
      ...program.overview,
      stats: program.overview.stats.map((s) => ({ ...s })),
      features: [...program.overview.features],
    },
    challenges: {
      ...program.challenges,
      items: program.challenges.items.map((i) => ({ ...i })),
    },
    strategy: {
      ...program.strategy,
      items: program.strategy.items.map((i) => ({ ...i })),
    },
    seo: { ...program.seo },
    hero: { ...program.hero },
    ...(program.apply
      ? {
          apply: {
            ...program.apply,
            steps: program.apply.steps.map((s) => ({ ...s })),
          },
        }
      : {}),
  })),
};

/** Structural blank for unknown CMS slugs — never inherit healthcare copy. */
export const EMPTY_PROGRAM_CONTENT: ProgramsContent["programs"][number] = {
  slug: "new-program",
  title: "New program",
  shortDescription: "Describe this program for the public listing.",
  features: [],
  image: "/images/program-healthcare.png",
  hero: {
    headline: "Program",
    accent: "Title",
    subtext: "Add a short hero subtext for this program.",
  },
  overview: {
    title: "Overview",
    icon: "📋",
    description: "Describe the program overview.",
    stats: [],
    features: [],
  },
  challenges: {
    title: "Challenges we address",
    icon: "⚡",
    items: [],
  },
  strategy: {
    title: "Our approach",
    icon: "🎯",
    items: [],
  },
  seo: {
    title: "New program | AJA Abhaya Hastham Foundation",
    description: "Describe this program for the public listing.",
    ogImage: "/images/program-healthcare.png",
  },
};

export const DEFAULT_IMPACT_CONTENT: ImpactContent = {
  hero: {
    eyebrow: "Our Impact",
    title: "Where We Create Lasting Change",
    accentWord: "Lasting",
    subtitle:
      "Explore six areas of work — from healthcare and education to goshala care and skill development — and the programs behind each.",
    imageSrc: assets.impactHero,
    imageAlt: "Community impact work in the field",
  },
  categories: staticImpactCategories.map((category) => ({
    id: category.id,
    slug: category.slug,
    summary: category.summary,
    programs: [...category.programs],
    image: category.image,
  })),
  gallery: {
    title: "In the Field",
    subtitle: "Moments from programs and community outreach.",
    images: assets.fieldWork.map((src, index) => ({
      id: `impact-g${index + 1}`,
      src,
      alt: `Field work moment ${index + 1}`,
      sortOrder: index,
    })),
  },
};

export function mergeProgramsContent(
  partial?: Partial<ProgramsContent> | null
): ProgramsContent {
  if (!partial?.programs?.length) {
    return {
      programs: DEFAULT_PROGRAMS_CONTENT.programs.map((p) => ({
        ...p,
        features: [...p.features],
        overview: {
          ...p.overview,
          stats: p.overview.stats.map((s) => ({ ...s })),
          features: [...p.overview.features],
        },
        challenges: {
          ...p.challenges,
          items: p.challenges.items.map((i) => ({ ...i })),
        },
        strategy: {
          ...p.strategy,
          items: p.strategy.items.map((i) => ({ ...i })),
        },
        seo: { ...p.seo },
        hero: { ...p.hero },
        ...(p.apply
          ? {
              apply: {
                ...p.apply,
                steps: p.apply.steps.map((s) => ({ ...s })),
              },
            }
          : {}),
      })),
    };
  }

  const bySlug = new Map(
    DEFAULT_PROGRAMS_CONTENT.programs.map((p) => [p.slug, p])
  );

  return {
    programs: partial.programs.map((program) => {
      const fallback = bySlug.get(program.slug) ?? EMPTY_PROGRAM_CONTENT;
      return {
        slug: nonEmpty(program.slug, fallback.slug),
        title: nonEmpty(program.title, fallback.title),
        shortDescription: nonEmpty(
          program.shortDescription,
          fallback.shortDescription
        ),
        features:
          program.features?.length > 0
            ? [...program.features]
            : [...fallback.features],
        image: isUsableImageUrl(program.image)
          ? program.image.trim()
          : fallback.image,
        hero: {
          headline: nonEmpty(program.hero?.headline, fallback.hero.headline),
          accent: nonEmpty(program.hero?.accent, fallback.hero.accent),
          subtext: nonEmpty(program.hero?.subtext, fallback.hero.subtext),
        },
        overview: {
          title: nonEmpty(program.overview?.title, fallback.overview.title),
          icon: nonEmpty(program.overview?.icon, fallback.overview.icon),
          description: nonEmpty(
            program.overview?.description,
            fallback.overview.description
          ),
          stats:
            program.overview?.stats?.length > 0
              ? program.overview.stats.map((s) => ({ ...s }))
              : fallback.overview.stats.map((s) => ({ ...s })),
          features:
            program.overview?.features?.length > 0
              ? [...program.overview.features]
              : program.features?.length > 0
                ? [...program.features]
                : [...fallback.overview.features],
        },
        challenges: {
          title: nonEmpty(
            program.challenges?.title,
            fallback.challenges.title
          ),
          icon: nonEmpty(program.challenges?.icon, fallback.challenges.icon),
          items:
            program.challenges?.items?.length > 0
              ? program.challenges.items.map((i) => ({ ...i }))
              : fallback.challenges.items.map((i) => ({ ...i })),
        },
        strategy: {
          title: nonEmpty(program.strategy?.title, fallback.strategy.title),
          icon: nonEmpty(program.strategy?.icon, fallback.strategy.icon),
          items:
            program.strategy?.items?.length > 0
              ? program.strategy.items.map((i) => ({ ...i }))
              : fallback.strategy.items.map((i) => ({ ...i })),
        },
        seo: {
          title: nonEmpty(program.seo?.title, fallback.seo.title),
          description: nonEmpty(
            program.seo?.description,
            fallback.seo.description
          ),
          ogImage: isUsableImageUrl(program.seo?.ogImage)
            ? program.seo!.ogImage.trim()
            : fallback.seo.ogImage,
        },
        ...(program.apply || fallback.apply
          ? {
              apply: {
                title: nonEmpty(
                  program.apply?.title,
                  fallback.apply?.title ?? "How to apply"
                ),
                intro: nonEmpty(
                  program.apply?.intro,
                  fallback.apply?.intro ?? ""
                ),
                steps:
                  (program.apply?.steps?.length
                    ? program.apply.steps
                    : fallback.apply?.steps) ?? [],
                ...(program.apply?.note || fallback.apply?.note
                  ? {
                      note: nonEmpty(
                        program.apply?.note,
                        fallback.apply?.note ?? ""
                      ),
                    }
                  : {}),
              },
            }
          : {}),
      };
    }),
  };
}

export function programsContentToSummaries(content: ProgramsContent) {
  return content.programs.map(
    ({ slug, title, shortDescription, features, image }) => ({
      slug,
      title,
      description: shortDescription,
      features,
      image,
    })
  );
}

export function programsContentToProgramList(
  content: ProgramsContent
): Program[] {
  return content.programs as Program[];
}

export function getMergedProgramBySlug(
  content: ProgramsContent,
  slug: string
): Program | undefined {
  return programsContentToProgramList(content).find((p) => p.slug === slug);
}

export function mergeImpactContent(
  partial?: Partial<ImpactContent> | null
): ImpactContent {
  const d = DEFAULT_IMPACT_CONTENT;
  return {
    hero: {
      eyebrow: nonEmpty(partial?.hero?.eyebrow, d.hero.eyebrow),
      title: nonEmpty(partial?.hero?.title, d.hero.title),
      accentWord: nonEmpty(partial?.hero?.accentWord, d.hero.accentWord),
      subtitle: nonEmpty(partial?.hero?.subtitle, d.hero.subtitle),
      imageSrc: isUsableImageUrl(partial?.hero?.imageSrc)
        ? partial!.hero!.imageSrc.trim()
        : d.hero.imageSrc,
      imageAlt: nonEmpty(partial?.hero?.imageAlt, d.hero.imageAlt),
    },
    categories:
      partial?.categories && partial.categories.length > 0
        ? partial.categories.map((category, index) => {
            const fallback = d.categories[index % d.categories.length];
            return {
              id: nonEmpty(category.id, fallback.id),
              slug: nonEmpty(category.slug, fallback.slug),
              summary: nonEmpty(category.summary, fallback.summary),
              programs:
                category.programs?.length > 0
                  ? [...category.programs]
                  : [...fallback.programs],
              image: isUsableImageUrl(category.image)
                ? category.image.trim()
                : fallback.image,
            };
          })
        : d.categories.map((c) => ({
            ...c,
            programs: [...c.programs],
          })),
    gallery: {
      title: nonEmpty(partial?.gallery?.title, d.gallery.title),
      subtitle: nonEmpty(partial?.gallery?.subtitle, d.gallery.subtitle),
      images:
        partial?.gallery?.images && partial.gallery.images.length > 0
          ? partial.gallery.images
              .map((img, index) => ({
                id: nonEmpty(img.id, `impact-g${index + 1}`),
                src: isUsableImageUrl(img.src)
                  ? img.src.trim()
                  : d.gallery.images[index % d.gallery.images.length]?.src ?? "",
                alt: nonEmpty(img.alt, `Field work moment ${index + 1}`),
                sortOrder:
                  typeof img.sortOrder === "number" ? img.sortOrder : index,
              }))
              .filter((img) => img.src)
              .sort((a, b) => a.sortOrder - b.sortOrder)
          : d.gallery.images.map((img) => ({ ...img })),
    },
  };
}
